import { Dropdown, DropdownProps } from 'components/dropdown/Dropdown';
import { SectionHeader } from 'components/expandable_pane/PaneSection';
import { TextInput, TextInputProps } from 'components/forms/TextInput';
import { DropdownValueWrapper } from 'components/inline_edit/DropdownInlineEditor';
import { BaseInlineEditorProps, InlineEditor } from 'components/inline_edit/InlineEditor';
import { NoValue } from 'components/no_value/NoValue';
import { useInlineEditor } from 'custom_hooks/useInlineEditor';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 } from 'uuid';

export type CustomInputConfig<TValue extends string> = {
    dropdownValue: TValue | undefined;
    custom?: string;
};

export type OptionChild<TValue> = {
    Value: TValue;
    Text: string;
};

const DropdownCustomValueWrapper = styled(DropdownValueWrapper)`
    flex-wrap: wrap;
    white-space: normal;
`;

export type DropdownCustomInputInlineEditorProps<TValue extends string> = Omit<
    BaseInlineEditorProps<CustomInputConfig<TValue>, string>,
    'value'
> &
    Readonly<{
        customOption: TValue;
        textInputConfig?: Partial<
            Omit<TextInputProps, 'value' | 'onChange' | 'renderValue'>
        > & { customLabel?: string };
        dropdownConfig: Omit<
            DropdownProps<OptionChild<TValue>>,
            'value' | 'onChange' | 'controlRef' | 'trigger' | 'renderValue'
        >;
        values: CustomInputConfig<TValue>;
    }>;

export function DropdownCustomInputInlineEditor<TValue extends string>({
    values,
    validate,
    onConfirm,
    onClose,
    renderValue,
    isDisabled,
    dropdownConfig: { data: options },
    dropdownConfig,
    customOption,
    textInputConfig,
}: DropdownCustomInputInlineEditorProps<TValue>) {
    const [id] = useState(() => `pane-section-header-${v4()}`);
    const popupRef = useRef<HTMLDivElement>(null);
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const {
        valueToRender,
        inputValue,
        error,
        setInputValue,
        isEditing,
        isConfirming,
        openEditor,
        closeEditor,
        confirm,
        canConfirm,
    } = useInlineEditor<CustomInputConfig<TValue>>({
        value: values,
        validate,
        onConfirm,
        onClose,
    });

    const displayText = () => {
        if (valueToRender?.custom !== undefined) {
            return valueToRender.custom;
        }
        return getDropdownText(valueToRender?.dropdownValue);
    };

    const getDropdownText = (value: TValue | undefined) => {
        if (!value) {
            return undefined;
        }
        if (Array.isArray(options)) {
            return options.find((o) => o.Value === value)?.Text;
        }
        return options?.items?.find((o) => o.Value === value)?.Text;
    };

    return (
        <InlineEditor
            value={
                <DropdownCustomValueWrapper>
                    {renderValue
                        ? renderValue(valueToRender)
                        : (displayText() ?? <NoValue />)}
                </DropdownCustomValueWrapper>
            }
            editor={
                <>
                    <Dropdown<OptionChild<TValue>>
                        {...dropdownConfig}
                        data={options}
                        value={
                            inputValue?.dropdownValue === undefined
                                ? undefined
                                : {
                                      Value: inputValue.dropdownValue,
                                      Text:
                                          getDropdownText(inputValue.dropdownValue) ?? '',
                                  }
                        }
                        onChange={(e) =>
                            setInputValue((current) => ({
                                ...current,
                                dropdownValue: e.Value,
                            }))
                        }
                        isDisabled={isDisabled}
                        trigger={{ className: 'full-width' }}
                        popup={{
                            popupRef,
                            ...dropdownConfig.popup,
                        }}
                    />
                    {inputValue?.dropdownValue === customOption && (
                        <>
                            {textInputConfig?.customLabel && (
                                <SectionHeader id={id} className="bold my-10">
                                    {textInputConfig.customLabel}
                                </SectionHeader>
                            )}
                            <TextInput
                                value={inputValue.custom}
                                onChange={(e) =>
                                    setInputValue(() => ({
                                        dropdownValue: customOption,
                                        custom: e.target.value,
                                    }))
                                }
                                invalid={hasAttemptedSave && error !== undefined}
                                disabled={isDisabled}
                                width="100%"
                                autofocus
                                required
                                {...textInputConfig}
                            />
                        </>
                    )}
                </>
            }
            error={hasAttemptedSave ? error : undefined}
            canClose={!isConfirming}
            canEdit={!isConfirming}
            canConfirm={!hasAttemptedSave || canConfirm}
            isEditing={isEditing}
            isDisabled={isDisabled || isConfirming}
            isPending={isConfirming}
            onClose={() => {
                setHasAttemptedSave(false);
                closeEditor();
            }}
            onEdit={openEditor}
            onConfirm={() => {
                setHasAttemptedSave(true);
                confirm();
            }}
            contentRefs={[popupRef]}
        />
    );
}
